import React, { useState, useEffect, useCallback } from 'react';
import MainLayout from '../components/layout/MainLayout';
import { 
    FiShield, FiGlobe, FiCheckCircle, FiXCircle, 
    FiActivity, FiZap, FiServer, FiTrash2, FiSearch, FiPlus, FiClock
} from 'react-icons/fi';
import { toast } from 'react-toastify';
import apiClient from '../api/apiClient';

export default function SecurityGatePage() {
    const [pendingRequests, setPendingRequests] = useState([]);
    const [authorizedDomains, setAuthorizedDomains] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    const [newDomain, setNewDomain] = useState('');

    // ⚔️ OPERAÇÃO: CARREGAR FIREWALL DO SNIPER (7702)
    const loadOrigins = useCallback(async (isAutoRefresh = false) => {
        try {
            if (!isAutoRefresh) setLoading(true);
            
            // Faz a chamada ao Sniper Security
            const response = await apiClient.get('/sniper/origins');
            
            // 🔥 BLINDAGEM: Garante que os dados sejam um Array antes do processamento
            const allOrigins = Array.isArray(response.data) ? response.data : [];
            
            // Filtramos em tempo real as frentes de batalha
            setPendingRequests(allOrigins.filter(o => o && o.status === 'pendente'));
            setAuthorizedDomains(allOrigins.filter(o => o && o.status === 'autorizado'));
            
        } catch (err) {
            console.error("❌ Erro Sniper:", err);
            if (!isAutoRefresh) toast.error("Radar Sniper Offline ou erro na resposta.");
        } finally {
            setLoading(false);
        }
    }, []);

    // Sincronização em ciclo curto (10 segundos) para detectar invasões em tempo real
    useEffect(() => {
        loadOrigins();
        const interval = setInterval(() => loadOrigins(true), 10000);
        return () => clearInterval(interval);
    }, [loadOrigins]);

    // ⚔️ OPERAÇÃO: AUTORIZAÇÃO MANUAL (VIA GERAR)
    const handleManualRegister = async (e) => {
        e.preventDefault();
        if (!newDomain) return;
        try {
            await apiClient.post('/sniper/origins/manual', { domain: newDomain.toLowerCase().trim() });
            toast.success(`ALVO AUTORIZADO: ${newDomain}`);
            setNewDomain('');
            loadOrigins(true);
        } catch (err) {
            toast.error("Falha ao injetar domínio manualmente.");
        }
    };

    // ⚔️ OPERAÇÃO: MUDANÇA DE STATUS (Aprovar/Bloquear)
    const handleStatusUpdate = async (id, newStatus, domainUrl) => {
        try {
            await apiClient.patch(`/sniper/origins/${id}`, { status: newStatus });
            newStatus === 'autorizado' 
                ? toast.success(`ACESSO LIBERADO: ${domainUrl}`)
                : toast.warn(`PERÍMETRO BLOQUEADO: ${domainUrl}`);
            loadOrigins(true);
        } catch (err) {
            toast.error("Erro na operação de segurança.");
        }
    };

    const filteredAuthorized = authorizedDomains.filter(d => 
        d.domain && d.domain.toLowerCase().includes(searchTerm.toLowerCase())
    );

    return (
        <MainLayout>
            <div className="max-w-7xl mx-auto p-4 md:p-8 space-y-8 animate-in fade-in duration-500">
                
                {/* HEADER TÁTICO */}
                <header className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 border-b border-gray-800 pb-8">
                    <div>
                        <h1 className="text-3xl font-black text-white flex items-center gap-3 uppercase tracking-tighter">
                            <FiShield className="text-green-500" /> Sniper Security Gate
                        </h1>
                        <p className="text-gray-500 text-[10px] font-bold uppercase tracking-widest mt-2 flex items-center gap-2">
                            <span className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></span>
                            Whitelist Operacional - Datacenter Curitiba
                        </p>
                    </div>
                    <div className="bg-black/50 border border-gray-800 px-6 py-3 rounded-2xl flex items-center gap-4">
                        <div className="flex flex-col items-end">
                            <span className="text-[9px] font-black text-gray-500 uppercase">Status do Sistema</span>
                            <span className="text-[11px] font-bold text-white uppercase tracking-widest">
                                {loading ? 'Sincronizando...' : 'Escuta Ativa'}
                            </span>
                        </div>
                        <FiActivity className={`text-2xl ${loading ? 'text-yellow-500 animate-spin' : 'text-green-500 animate-pulse'}`} />
                    </div>
                </header>

                <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
                    
                    {/* RADAR DE REQUISIÇÕES (ESQUERDA) */}
                    <div className="lg:col-span-7 space-y-6">
                        <div className="flex items-center justify-between px-2">
                            <h2 className="text-white text-xs font-black uppercase tracking-widest flex items-center gap-2">
                                <FiZap className="text-yellow-500" /> Requisições Detectadas (Radar)
                            </h2>
                            <span className="text-[10px] bg-yellow-500/10 text-yellow-500 px-3 py-1 rounded-full font-bold border border-yellow-500/20">
                                {pendingRequests.length} ALVOS EM ANÁLISE
                            </span>
                        </div>

                        <div className="space-y-4 max-h-[600px] overflow-y-auto pr-2 custom-scrollbar">
                            {pendingRequests.map((req) => (
                                <div key={req._id} className="bg-gray-900/50 border-l-4 border-yellow-500 rounded-2xl p-6 border border-gray-800 hover:bg-gray-800/40 transition-all group relative overflow-hidden">
                                    <div className="flex justify-between items-center relative z-10">
                                        <div className="space-y-3">
                                            <div className="flex items-center gap-3">
                                                <div className="p-2 bg-cyan-500/10 rounded-lg text-cyan-500">
                                                    <FiGlobe size={20} />
                                                </div>
                                                <span className="text-white font-mono text-lg font-black tracking-tight">{req.domain}</span>
                                            </div>
                                            <div className="flex gap-6 text-[9px] font-black uppercase text-gray-500">
                                                <span className="flex items-center gap-1"><FiServer /> IP: <b className="text-gray-300">{req.ip || '0.0.0.0'}</b></span>
                                                <span className="flex items-center gap-1"><FiClock /> Detectado: <b className="text-gray-300">{new Date(req.requestedAt).toLocaleTimeString()}</b></span>
                                            </div>
                                        </div>
                                        <div className="flex gap-3">
                                            <button 
                                                onClick={() => handleStatusUpdate(req._id, 'autorizado', req.domain)}
                                                className="bg-green-600 hover:bg-green-500 text-white px-5 py-3 rounded-xl transition-all shadow-lg shadow-green-900/20 font-black text-[10px] uppercase flex items-center gap-2">
                                                <FiCheckCircle size={16} /> Autorizar
                                            </button>
                                            <button 
                                                onClick={() => handleStatusUpdate(req._id, 'bloqueado', req.domain)}
                                                className="bg-gray-800 hover:bg-red-600 text-white p-3 rounded-xl transition-all">
                                                <FiXCircle size={18} />
                                            </button>
                                        </div>
                                    </div>
                                    <div className="absolute top-0 right-0 p-1 bg-yellow-500/10 text-yellow-500 text-[8px] font-bold uppercase tracking-tighter rounded-bl-lg">Pendente</div>
                                </div>
                            ))}
                            {pendingRequests.length === 0 && (
                                <div className="p-16 border-2 border-dashed border-gray-800 rounded-[3rem] text-center bg-gray-900/20">
                                    <FiShield className="mx-auto text-gray-800 text-5xl mb-4" />
                                    <p className="text-gray-600 text-[10px] font-bold uppercase tracking-[0.2em]">Perímetro Limpo: Nenhuma invasão detectada</p>
                                </div>
                            )}
                        </div>
                    </div>

                    {/* WHITELIST ATIVA (DIREITA) */}
                    <div className="lg:col-span-5 space-y-6">
                        <div className="bg-gray-900/40 p-6 rounded-[2.5rem] border border-gray-800 space-y-6">
                            <h2 className="text-white text-xs font-black uppercase tracking-widest flex items-center gap-2 px-2">
                                <FiPlus className="text-green-500" /> Injeção Manual de Domínio
                            </h2>
                            <form onSubmit={handleManualRegister} className="flex gap-2 bg-black/60 p-2 rounded-2xl border border-gray-700">
                                <input 
                                    type="text" 
                                    placeholder="EX: imobras.com.br" 
                                    className="bg-transparent border-none text-[11px] text-white outline-none flex-1 px-4 font-bold uppercase"
                                    value={newDomain}
                                    onChange={(e) => setNewDomain(e.target.value)}
                                />
                                <button type="submit" className="bg-green-600 hover:bg-green-500 text-white p-3 rounded-xl transition-all flex items-center gap-2 px-6 text-[10px] font-black uppercase shadow-lg shadow-green-900/40">
                                    Gerar
                                </button>
                            </form>
                        </div>

                        <div className="bg-gray-900/40 border border-gray-800 rounded-[2.5rem] overflow-hidden flex flex-col h-[500px]">
                            <div className="p-6 bg-black/40 border-b border-gray-800 flex items-center gap-3">
                                <FiSearch className="text-cyan-500" />
                                <input 
                                    type="text" 
                                    value={searchTerm}
                                    onChange={(e) => setSearchTerm(e.target.value)}
                                    placeholder="Filtrar Whitelist..." 
                                    className="bg-transparent border-none text-[11px] text-white outline-none w-full font-bold uppercase" 
                                />
                            </div>
                            
                            <div className="overflow-y-auto divide-y divide-gray-800/50 flex-1 custom-scrollbar">
                                {filteredAuthorized.map((domain) => (
                                    <div key={domain._id} className="p-5 hover:bg-white/5 transition-all flex items-center justify-between group">
                                        <div className="flex flex-col gap-1">
                                            <span className="text-sm text-white font-black tracking-tight">{domain.domain}</span>
                                            <div className="flex items-center gap-2">
                                                <span className="text-[8px] bg-green-500 text-black px-2 py-0.5 rounded font-black uppercase">Ativo</span>
                                                <span className="text-[8px] text-gray-500 font-bold uppercase tracking-tighter">
                                                    Desde {new Date(domain.requestedAt).toLocaleDateString()}
                                                </span>
                                            </div>
                                        </div>
                                        <button 
                                            onClick={() => handleStatusUpdate(domain._id, 'bloqueado', domain.domain)}
                                            className="text-gray-600 hover:text-red-500 transition-all p-3 hover:bg-red-500/10 rounded-xl" title="Revogar Acesso">
                                            <FiTrash2 size={18} />
                                        </button>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>

                </div>
            </div>
        </MainLayout>
    );
}